import { useQuery } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';

/**
 * [coupon-audit] 쿠폰별 발급 내역 조회 — BE GET /api/admin/v1/coupons/{couponId}/issued
 * (AdminCouponController.getIssuedList → Page<CouponIssuedSummary>). 종전 BO 에 이 endpoint
 * 소비 UI 가 전무했다(발급 현황 확인 불가). 경로가 generated/paths 타입에 미반영이라
 * adminFetch 로 직접 호출(관례). RBAC: SUPER_ADMIN/OPS_ADMIN.
 *
 * 사용 상태: be-snapshot additive 확장 — status(ISSUED|USED|RESTORED)/orderId/redeemedAt
 * (orderId·redeemedAt 은 USED 일 때만 non-null). 미배포 BE 응답은 키 생략 → optional 방어.
 */
export type CouponIssuedStatus = 'ISSUED' | 'USED' | 'RESTORED';

export interface CouponIssuedRow {
  readonly id: string;
  readonly userId: string;
  readonly badge: string;
  readonly title: string;
  readonly discountAmount: number;
  readonly conditionAmount: number;
  readonly expiresAt: string | null;
  readonly downloadable: boolean;
  readonly status?: CouponIssuedStatus | null;
  readonly orderId?: string | null;
  readonly redeemedAt?: string | null;
}

export interface CouponIssuedPage {
  readonly items: ReadonlyArray<CouponIssuedRow>;
  readonly totalItems: number;
  readonly totalPages: number;
}

interface IssuedEnvelope {
  success: boolean;
  data?: {
    content?: CouponIssuedRow[];
    items?: CouponIssuedRow[];
    totalElements?: number;
    totalPages?: number;
  } | null;
  meta?: {
    pagination?: { totalItems?: number; totalPages?: number } | null;
  } | null;
}

export const couponIssuedQueryKey = (couponId: string, page: number) =>
  ['admin', 'coupon-issued', couponId, page] as const;

export const useCouponIssuedQuery = (couponId: string | null, page: number, size = 20) =>
  useQuery<CouponIssuedPage>({
    queryKey: couponIssuedQueryKey(couponId ?? '', page),
    enabled: Boolean(couponId),
    queryFn: async () => {
      // UI 1-based → Spring Pageable 0-based.
      const res = await adminFetch(
        `/api/admin/v1/coupons/${couponId as string}/issued?page=${Math.max(0, page - 1)}&size=${size}`,
        {  },
      );
      if (!res.ok) throw new Error(`발급 내역 조회 실패: ${res.status}`);
      const env = (await res.json()) as IssuedEnvelope;
      const items = env.data?.content ?? env.data?.items ?? [];
      return {
        items,
        totalItems: env.meta?.pagination?.totalItems ?? env.data?.totalElements ?? items.length,
        totalPages: env.meta?.pagination?.totalPages ?? env.data?.totalPages ?? 1,
      };
    },
  });
